import React, { useState } from 'react';
import { X, Heart, Trash2 } from 'lucide-react';

function LikesPanel({ likes = [], currentUserUuid, isContentOwner, onToggleLike, onClose }) {
  const [toggling, setToggling] = useState(false);

  const currentUserHasLiked = likes.some(l => l.userUuid === currentUserUuid);

  const sortedLikes = [...likes].sort((a, b) => new Date(b.created) - new Date(a.created));

  const handleToggle = async () => {
    if (toggling) return;
    setToggling(true);
    try {
      await onToggleLike();
    } catch (error) {
      console.error('Failed to toggle like:', error);
    }
    setToggling(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="absolute inset-0 z-40 flex items-end" onClick={onClose}>
      <div
        className="w-full max-h-[60%] bg-gray-800 rounded-t-2xl flex flex-col animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h3 className="text-white font-semibold">Likes ({likes.length})</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-700 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Likes List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {sortedLikes.length === 0 ? (
            <div className="text-center py-8">
              <Heart className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400 font-medium">No likes yet</p>
              <p className="text-gray-500 text-sm mt-1">Be the first to show some love.</p>
            </div>
          ) : (
            sortedLikes.map((like) => (
              <div key={like.uuid} className="flex items-center gap-3 py-1">
                <Heart className="w-4 h-4 text-red-500 fill-red-500 flex-shrink-0" />
                <span className="text-white text-sm font-medium flex-1">{like.userName}</span>
                <span className="text-gray-500 text-xs">{formatDate(like.created)}</span>
              </div>
            ))
          )}
        </div>

        {/* Toggle Like Button */}
        <div className="p-3 border-t border-gray-700">
          <button
            onClick={handleToggle}
            disabled={toggling}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl transition-colors disabled:opacity-50 hover:bg-gray-700"
          >
            <Heart className={`w-5 h-5 ${currentUserHasLiked ? 'text-red-500 fill-red-500' : 'text-white'}`} />
            <span className="text-white font-medium text-sm">
              {currentUserHasLiked ? 'Remove your like' : 'Like this memory'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default LikesPanel;
